import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { TutorialPages } from '../../../common/src/model.ts';
import { getOnboardingPath, getTutorialPath, OnboardingPages, Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import {
  decideSubscriptionBanner,
  type SubscriptionBannerDecision,
} from '../../../common/src/subscription_banner_policy.ts';
import { ensureExists } from '../../../common/src/util.ts';
import StopwatchIcon from '../assets/stopwatch.svg?react';
import { synchronizeOrgsWithServer, useCurrentOrg } from '../data/use_current_org.tsx';
import { synchronizeHouseholdWithServer, useHousehold } from '../data/use_household.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { isOptimisticallySubscribed } from '../util/optimistic_subscription.ts';
import { getPageFromLocation, getUrlForPage } from '../util/path.ts';
import { storeProspectiveSubscriptionOrigin } from '../util/subscription_flow_origin.ts';
import { useCurrentDate } from '../util/use_current_date.tsx';
import { attentionBannerBackground, attentionBannerDarkBackground } from './banner_colors.ts';
import { shareWithMusicLeader } from './onboarding/common.tsx';

function useBannerDecision(): SubscriptionBannerDecision {
  const location = useLocation();
  const page = getPageFromLocation(location);
  const { currentOrg } = useCurrentOrg();
  const { household } = useHousehold();
  const { isInternalUser } = useUserAttributes();
  const clientDate = useCurrentDate();

  return decideSubscriptionBanner({
    page,
    organization: currentOrg,
    household,
    clientDate,
    isBypassUser: isInternalUser,
    isOptimisticallySubscribed: isOptimisticallySubscribed({ household }),
  });
}

type StandardBannerVariant = Extract<SubscriptionBannerDecision['variant'], 'subscribe' | 'invite'>;
type ActionBannerVariant = Extract<SubscriptionBannerDecision['variant'], 'subscribe' | 'opt-in' | 'invite'>;

const bannerMessages: Record<StandardBannerVariant, string> = {
  subscribe: 'Subscribe your household to play all music.',
  invite: 'Add your church to play all music.',
};

export function SubscriptionBanner() {
  const decision = useBannerDecision();
  const navigate = useNavigate();
  const location = useLocation();
  const page = getPageFromLocation(location);
  const { currentOrg } = useCurrentOrg();
  const { household } = useHousehold();
  const householdToken = household?.token;

  const onStartFreeTrial = useCallback(async () => {
    storeProspectiveSubscriptionOrigin({ originPage: page });
    await navigate(getUrlForPage(Pages.SubscriptionTrial));
  }, [navigate, page]);

  const onOptIn = useCallback(async () => {
    await server_api.optHouseholdIntoChurchSubscription({
      householdToken: ensureExists(householdToken),
      churchToken: ensureExists(currentOrg?.token),
    });
    await synchronizeHouseholdWithServer({ force: true });
    await synchronizeOrgsWithServer({ force: true });
  }, [householdToken, currentOrg]);

  const onInviteChurch = useCallback(async () => {
    await shareWithMusicLeader();
  }, []);

  const onTryTutorial = useCallback(async () => {
    await navigate(getTutorialPath(TutorialPages.FamilyWorship));
  }, [navigate]);

  if (decision.variant === 'hidden') {
    return null;
  }

  if (decision.variant === 'active-trial') {
    return <TrialBanner variant={decision.variant} daysLeft={decision.daysLeft} />;
  }

  if (decision.variant === 'expired-trial') {
    return <TrialBanner variant={decision.variant} />;
  }

  return (
    <BannerContainer>
      <SubscriptionBannerCopy variant={decision.variant} />
      <ButtonRow>
        {decision.variant === 'subscribe' && <PrimaryButton onClick={onStartFreeTrial}>Start Free Trial</PrimaryButton>}
        {decision.variant === 'opt-in' && <PrimaryButton onClick={onOptIn}>Opt In</PrimaryButton>}
        {decision.variant === 'invite' && <PrimaryButton onClick={onInviteChurch}>Invite Your Church</PrimaryButton>}
        {decision.variant !== 'opt-in' && <SecondaryButton onClick={onTryTutorial}>Try the Tutorial</SecondaryButton>}
      </ButtonRow>
    </BannerContainer>
  );
}

function SubscriptionBannerCopy({ variant }: { variant: ActionBannerVariant }) {
  if (variant === 'opt-in') {
    return (
      <BannerMessage>
        Your church provides full subscriptions
        <br />
        to all members.
      </BannerMessage>
    );
  }

  return <BannerMessage>{bannerMessages[variant]}</BannerMessage>;
}

function TrialBanner({
  variant,
  daysLeft,
}: {
  variant: Extract<SubscriptionBannerDecision['variant'], 'active-trial' | 'expired-trial'>;
  daysLeft?: number;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const page = getPageFromLocation(location);
  const onSubscribe = useCallback(async () => {
    storeProspectiveSubscriptionOrigin({ originPage: page });
    await navigate(getOnboardingPath(OnboardingPages.SubscribeStart));
  }, [navigate, page]);

  const message =
    variant === 'active-trial'
      ? `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your trial`
      : 'Your free trial has ended';

  return (
    <TrialBannerContainer $variant={variant}>
      <TrialBannerMessage>
        {variant === 'active-trial' && <TrialClockIcon aria-hidden="true" />}
        {message}
      </TrialBannerMessage>
      <SubscribeLink onClick={onSubscribe}>Subscribe</SubscribeLink>
    </TrialBannerContainer>
  );
}

const BannerContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  text-align: center;
`;

const BannerMessage = styled.div`
  font-size: 1em;
  color: var(--color-text);
  max-width: 38ch;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const PrimaryButton = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1rem;
  color: white;
  background-color: var(--color-primary-button);
  padding: 6px 16px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  text-wrap: nowrap;
`;

const SecondaryButton = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 1rem;
  color: var(--color-primary-button);
  background-color: white;
  padding: 6px 16px;
  border-radius: 30px;
  border: 2px solid var(--color-primary-button);
  cursor: pointer;
  text-wrap: nowrap;
`;

const TrialBannerContainer = styled.div<{ $variant: 'active-trial' | 'expired-trial' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 10px 16px;
  text-align: center;
  background-color: ${(props) => (props.$variant === 'active-trial' ? '#eeedfe' : attentionBannerBackground)};
  color: var(--color-text-black);

  [data-theme='dark'] & {
    background-color: ${(props) => (props.$variant === 'active-trial' ? '#383252' : attentionBannerDarkBackground)};
    color: var(--color-text);
  }
`;

const TrialBannerMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.95rem;
`;

const TrialClockIcon = styled(StopwatchIcon)`
  width: 18px;
  height: 18px;
  fill: currentColor;
  flex-shrink: 0;
`;

const SubscribeLink = styled.button`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 0.95rem;
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
`;
